/* eslint-disable sonarjs/no-duplicate-string */
import { Injectable } from '@angular/core';
import { ContentfulService } from '@cigna/shared/angular/contentful-util';
import {
  ContentfulAlertEntry,
  ContentfulAnnoucement,
  ContentfulAnnoucementEntry,
  ContentfulBannerEntry,
} from '@cigna/chcp/shared/contentful-util';

import { marked } from 'marked';
import { LOB } from '@cigna/chcp/auth/util';

function matchesEntitlements(
  fields: { entitlements?: string[] } | undefined,
  entitlements?: string[],
): boolean {
  return fields?.entitlements
    ? fields.entitlements.some((e) => entitlements?.includes(e))
    : true;
}

@Injectable({ providedIn: 'root' })
export class ChcpContentfulService {
  constructor(private contentfulService: ContentfulService) {}

  getAlerts(id: string, lob?: LOB, entitlements?: string[]) {
    return this.contentfulService.client.withoutUnresolvableLinks
      .getEntry<ContentfulAlertEntry>(id)
      .then((entry) =>
        entry.fields.generalContent
          .filter((c) => c?.sys.contentType.sys.id === 'alert')
          .filter((c) =>
            lob && c?.fields.lineOfBusiness.length
              ? c.fields.lineOfBusiness.includes(lob)
              : true,
          )
          .filter((c) => matchesEntitlements(c?.fields, entitlements))
          .map((alert) => ({
            id: alert?.sys.id || '',
            body: alert?.fields.alert
              ? marked
                  .parse(alert.fields.alert)
                  .replaceAll(
                    '<a',
                    '<a target="_blank" rel="noopener noreferrer"',
                  )
              : '',
          })),
      )
      .catch((error) => []);
  }

  getHomepageBanners(id: string, lob: string, entitlements?: string[]) {
    return this.contentfulService.client.withoutUnresolvableLinks
      .getEntry<ContentfulBannerEntry>(id)
      .then((entry) =>
        entry.fields.generalContent
          .filter((c) => c?.fields.lineOfBusiness.includes(lob))
          .filter((c) => matchesEntitlements(c?.fields, entitlements))
          .map((banner) => ({
            id: banner?.sys.id || '',
            title: banner?.fields.title,
            content: banner
              ? marked
                  .parse(banner.fields.description)
                  .replaceAll(
                    '<a',
                    '<a target="_blank" rel="noopener noreferrer"',
                  )
              : '',
            path: banner?.fields.ctaLink,
            resourceLink: banner?.fields.ctaLink?.startsWith(
              'https://static.cigna.com/assets/chcp',
            ),
            appLink: banner?.fields.ctaLink?.startsWith('/app'),
            linkText: banner?.fields.ctaLabel,
            imgUrl: `${banner?.fields.image?.fields.file?.url}?fm=webp`,
          })),
      )
      .catch((error) => []);
  }

  getLatestUpdatesProductArea(id: string, lob: string) {
    return this.contentfulService.client.withoutUnresolvableLinks
      .getEntry<ContentfulAnnoucementEntry>(id)
      .then((entries) =>
        entries.fields.generalContent
          .filter((c) => c?.fields.lineOfBusiness.includes(lob))
          .map((update) => ({
            id: update?.sys.id || '',
            category: '',
            subcategory: '',
            title: update?.fields.heading,
            date: update?.fields.displayDate, // todo date fns format to MM/DD/YY
            content: update?.fields.announcement
              ? marked
                  .parse(update.fields.announcement)
                  .replaceAll(
                    '<a',
                    '<a target="_blank" rel="noopener noreferrer"',
                  )
              : '',
            path: update?.fields.ctaLink,
            linkText: update?.fields.ctaLabel,
            openExternal: true,
            resourceLink: false,
            appLink: false,
            priority: 0,
          })),
      )
      .catch(() => [
        {
          id: 'update_error',
          category: '',
          subcategory: '',
          title: 'Error fetching updates',
          date: '', // todo date fns format to MM/DD/YY
          content: '',
          path: '',
          linkText: '',
          openExternal: true,
          resourceLink: false,
          appLink: false,
          priority: 0,
        },
      ]);
  }

  getLatestUpdates(lob: string) {
    return (
      this.contentfulService.client.withoutUnresolvableLinks
        .getEntries<ContentfulAnnoucement>({
          content_type: 'announcement',
          order: ['-fields.displayDate'],
        })
        .then((entries) =>
          entries.items
            .filter((c) => c.fields.lineOfBusiness.includes(lob))
            .map((update) => ({
              id: update.sys.id || '',
              category: '',
              subcategory: '',
              title: update.fields.heading,
              date: update.fields.displayDate, // todo date fns format to MM/DD/YY
              content: update.fields.announcement
                ? marked
                    .parse(update.fields.announcement)
                    .replaceAll(
                      '<a',
                      '<a target="_blank" rel="noopener noreferrer"',
                    )
                : '',
              path: update.fields.ctaLink,
              linkText: update.fields.ctaLabel,
              openExternal: true,
              resourceLink: false,
              appLink: false,
              priority: 0,
            })),
        )
        // eslint-disable-next-line sonarjs/no-identical-functions
        .catch(() => [
          {
            id: 'update_error',
            category: '',
            subcategory: '',
            title: 'Error fetching updates',
            date: '', // todo date fns format to MM/DD/YY
            content: '',
            path: '',
            linkText: '',
            openExternal: true,
            resourceLink: false,
            appLink: false,
            priority: 0,
          },
        ])
    );
  }
}
